import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import type { AppState, Notificaciones, Perfil, Preferencias } from '../types';
import { Field, PageShell, Toggle, glassLight, inputStyle, T, primaryButton } from '../ui';
function PanelLabel({
  children
}: {
  children: React.ReactNode;
}) {
  return <div style={{
    fontSize: 11,
    fontWeight: 700,
    color: T.textMuted,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8
  }} data-id="element-189">
      <span style={{
      width: 5,
      height: 5,
      borderRadius: '50%',
      background: T.accent,
      boxShadow: `0 0 8px ${T.accent}`
    }} data-id="element-190" />
      {children}
    </div>;
}
const panelVariant = {
  hidden: {
    opacity: 0,
    y: 14
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.34,
      ease: [0.2, 0.8, 0.2, 1]
    }
  }
};
type Tab = 'general' | 'usuarios' | 'roles';
export function Configuracion({
  state,
  setPerfil,
  setNotif,
  setPrefs,
  usuariosNode,
  rolesNode
}: {
  state: AppState;
  setPerfil: (value: Perfil) => void;
  setNotif: (value: Notificaciones) => void;
  setPrefs: (value: Preferencias) => void;
  usuariosNode?: React.ReactNode;
  rolesNode?: React.ReactNode;
}) {
  // ── Draft state local (no guarda hasta presionar "Guardar") ──────────────
  const [draftPerfil, setDraftPerfil] = useState<Perfil>({
    ...state.perfil
  });
  const [draftNotif, setDraftNotif] = useState<Notificaciones>({
    ...state.notif
  });
  const [draftPrefs, setDraftPrefs] = useState<Preferencias>({
    ...state.prefs
  });
  // Cuando cambia el usuario logueado (session), sincronizamos el draft
  useEffect(() => {
    setDraftPerfil({
      ...state.perfil
    });
    setDraftNotif({
      ...state.notif
    });
    setDraftPrefs({
      ...state.prefs
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.perfil.correo]);
  // ── Feedback de guardado ─────────────────────────────────────────────────
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  // Detectar si hay cambios pendientes
  const hasChanges = JSON.stringify(draftPerfil) !== JSON.stringify(state.perfil) || JSON.stringify(draftNotif) !== JSON.stringify(state.notif) || JSON.stringify(draftPrefs) !== JSON.stringify(state.prefs);
  const handleSave = () => {
    setSaving(true);
    setPerfil(draftPerfil);
    setNotif(draftNotif);
    setPrefs(draftPrefs);
    setTimeout(() => {
      setSaving(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }, 350);
  };
  // ── Tabs ──────────────────────────────────────────────────────────────────
  const tabs: {
    id: Tab;
    label: string;
    show: boolean;
  }[] = [{
    id: 'general',
    label: 'General',
    show: true
  }, {
    id: 'usuarios',
    label: 'Usuarios',
    show: !!usuariosNode
  }, {
    id: 'roles',
    label: 'Roles y permisos',
    show: !!rolesNode
  }];
  const visibles = tabs.filter(t => t.show);
  const [tab, setTab] = useState<Tab>('general');
  return <PageShell title="Configuración" sub="Perfil, notificaciones, usuarios, roles y conexión del sistema" data-id="element-191">
      {/* ── Sub-navegación ─────────────────────────────────────────────── */}
      {visibles.length > 1 && <div style={{
      display: 'flex',
      gap: 6,
      padding: 5,
      borderRadius: 14,
      background: T.surfaceSubtle,
      border: `1px solid ${T.border}`,
      width: 'fit-content',
      marginBottom: 20,
      flexWrap: 'wrap'
    }} data-id="element-192">
          {visibles.map(t => {
        const active = tab === t.id;
        return <button key={t.id} onClick={() => setTab(t.id)} style={{
          position: 'relative',
          padding: '9px 18px',
          borderRadius: 10,
          border: 'none',
          background: active ? T.accentSoft : 'transparent',
          color: active ? T.accent : T.textMuted,
          fontSize: 13,
          fontWeight: active ? 700 : 600,
          cursor: 'pointer'
        }} data-id="element-193">
                {t.label}
              </button>;
      })}
        </div>}

      {/* ── Tab General ──────────────────────────────────────────────────── */}
      {tab === 'general' && <>
          <motion.div initial="hidden" animate="show" variants={{
        hidden: {},
        show: {
          transition: {
            staggerChildren: 0.06
          }
        }
      }} style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit,minmax(290px,1fr))',
        gap: 16
      }} data-id="element-194">
            {/* ── Perfil ───────────────────────────────────────────────── */}
            <motion.div variants={panelVariant} style={{
          ...glassLight,
          padding: 20
        }} data-id="element-195">
              <PanelLabel data-id="element-196">Perfil</PanelLabel>

              <Field label="Nombre" data-id="element-197">
                <input className="getr-input" style={inputStyle} value={draftPerfil.nombre} onChange={e => setDraftPerfil({
              ...draftPerfil,
              nombre: e.target.value
            })} data-id="element-198" />
              </Field>
              <Field label="Correo" data-id="element-199">
                <input className="getr-input" style={inputStyle} value={draftPerfil.correo} onChange={e => setDraftPerfil({
              ...draftPerfil,
              correo: e.target.value
            })} data-id="element-200" />
              </Field>
              <Field label="Rol" data-id="element-201">
                <input className="getr-input" style={{
              ...inputStyle,
              color: T.textMuted
            }} value={draftPerfil.rol} readOnly data-id="element-202" />
              </Field>
            </motion.div>

            {/* ── Notificaciones ───────────────────────────────────────── */}
            <motion.div variants={panelVariant} style={{
          ...glassLight,
          padding: 20
        }} data-id="element-203">
              <PanelLabel data-id="element-204">Notificaciones</PanelLabel>
              <Toggle label="Alertas fuera de rango" checked={draftNotif.fueraRango} onChange={() => setDraftNotif({
            ...draftNotif,
            fueraRango: !draftNotif.fueraRango
          })} data-id="element-205" />
              <Toggle label="Resumen diario" checked={draftNotif.resumen} onChange={() => setDraftNotif({
            ...draftNotif,
            resumen: !draftNotif.resumen
          })} data-id="element-206" />
              <Toggle label="Cambios de auditoría" checked={draftNotif.auditoria} onChange={() => setDraftNotif({
            ...draftNotif,
            auditoria: !draftNotif.auditoria
          })} data-id="element-207" />
              <Toggle label="Enviar por correo" checked={draftNotif.email} onChange={() => setDraftNotif({
            ...draftNotif,
            email: !draftNotif.email
          })} data-id="element-208" />
            </motion.div>

            {/* ── Preferencias ─────────────────────────────────────────── */}
            <motion.div variants={panelVariant} style={{
          ...glassLight,
          padding: 20
        }} data-id="element-209">
              <PanelLabel data-id="element-210">Preferencias</PanelLabel>
              <Field label="Lux mínimo global" data-id="element-211">
                <input className="getr-input" style={inputStyle} type="number" value={draftPrefs.luxMin} onChange={e => setDraftPrefs({
              ...draftPrefs,
              luxMin: Number(e.target.value)
            })} data-id="element-212" />
              </Field>
              <Field label="Idioma" data-id="element-213">
                <select className="getr-input" style={inputStyle} value={draftPrefs.idioma} onChange={e => setDraftPrefs({
              ...draftPrefs,
              idioma: e.target.value
            })} data-id="element-214">
                  <option data-id="element-215">Español</option>
                  <option data-id="element-216">English</option>
                </select>
              </Field>
              <Field label="Tema" data-id="element-217">
                <select className="getr-input" style={inputStyle} value={draftPrefs.tema} onChange={e => setDraftPrefs({
              ...draftPrefs,
              tema: e.target.value
            })} data-id="element-218">
                  <option data-id="element-219">Oscuro</option>
                  <option data-id="element-220">Claro</option>
                  <option data-id="element-221">Automático</option>
                </select>
              </Field>
            </motion.div>
          </motion.div>

          {/* ── Botón Guardar ─────────────────────────────────────────── */}
          <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} style={{
        marginTop: 24,
        display: 'flex',
        alignItems: 'center',
        gap: 14
      }} data-id="element-222">
            <button onClick={handleSave} disabled={saving || !hasChanges} style={{
          ...primaryButton,
          opacity: !hasChanges || saving ? 0.5 : 1,
          cursor: !hasChanges || saving ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          transition: 'opacity 0.2s'
        }} data-id="element-223">
              {saving ? <>
                  <span style={{
              width: 14,
              height: 14,
              border: '2px solid rgba(255,255,255,0.3)',
              borderTopColor: '#ffffff',
              borderRadius: '50%',
              display: 'inline-block',
              animation: 'spin 0.7s linear infinite'
            }} data-id="element-224" />
                  Guardando…
                </> : <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" data-id="element-225">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" data-id="element-226" />
                    <polyline points="17 21 17 13 7 13 7 21" data-id="element-227" />
                    <polyline points="7 3 7 8 15 8" data-id="element-228" />
                  </svg>
                  Guardar cambios
                </>}
            </button>

            {/* Indicador de cambios pendientes */}
            {hasChanges && !saved && <motion.span initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} style={{
          fontSize: 12,
          color: T.textMuted
        }} data-id="element-229">
                Cambios sin guardar
              </motion.span>}

            {/* Confirmación de guardado */}
            {saved && <motion.span initial={{
          opacity: 0,
          x: -6
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0
        }} style={{
          fontSize: 13,
          fontWeight: 600,
          color: T.ok,
          display: 'flex',
          alignItems: 'center',
          gap: 6
        }} data-id="element-230">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" data-id="element-231">
                  <polyline points="20 6 9 17 4 12" data-id="element-232" />
                </svg>
                ¡Guardado correctamente!
              </motion.span>}
          </motion.div>

          {/* Keyframe para el spinner */}
          <style data-id="element-233">{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </>}

      {/* ── Tab Usuarios ─────────────────────────────────────────────────── */}
      {tab === 'usuarios' && usuariosNode && <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3
    }} data-id="element-234">
          {usuariosNode}
        </motion.div>}

      {/* ── Tab Roles ────────────────────────────────────────────────────── */}
      {tab === 'roles' && rolesNode && <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3
    }} data-id="element-235">
          {rolesNode}
        </motion.div>}
    </PageShell>;
}